'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { Button, ButtonSize, ButtonVariant, CodeSnippet, Icon, IconComponent } from '@graftcode/design-system'
import clsx from 'clsx'

import {
  GRAFTCODE_ONBOARDING_ASSETS_BASE,
  GRAFTCODE_ONBOARDING_SETUP_MODE,
  type GraftcodeOnboardingSetupMode,
} from '../../graftcode-onboarding.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import type { GraftcodeOnboardingStepMotion } from '../../hooks/useGraftcodeOnboardingGuideMotion'
import { getGraftcodeOnboardingStepElementId } from '../../hooks/useGraftcodeOnboardingGuideMotion'
import type { OnboardingRequirementChip } from '../../lib/graftcode-onboarding-requirements'
import type { OnboardingTutorialStep } from '../../lib/parseOnboardingTutorialMarkdown'

import { getTutorialCodeBlockKey, mapCodeLanguage } from './graftcode-onboarding-dashboard.helpers'
import { GraftcodeOnboardingAgentPrompt } from './GraftcodeOnboardingAgentPrompt'
import { GraftcodeOnboardingTimelineBadge } from './GraftcodeOnboardingTimelineBadge'

import styles from './GraftcodeOnboardingDashboard.module.scss'

type GraftcodeOnboardingDashboardTutorialStepsProps = {
  steps: OnboardingTutorialStep[]
  visibleStepIndexes: number[]
  activeStepIndex: number
  setupMode: GraftcodeOnboardingSetupMode
  requirementChips: OnboardingRequirementChip[]
  showTutorialButtonOnStep: (stepIndex: number) => boolean
  stepMotion: GraftcodeOnboardingStepMotion
  contentRefreshing: boolean
  onPrevious: () => void
  onNext: () => void
  onCompleteTutorial: () => void
  onReopenTutorialVideo: () => void
}

export function GraftcodeOnboardingDashboardTutorialSteps({
  steps,
  visibleStepIndexes,
  activeStepIndex,
  setupMode,
  requirementChips,
  showTutorialButtonOnStep,
  stepMotion,
  contentRefreshing,
  onPrevious,
  onNext,
  onCompleteTutorial,
  onReopenTutorialVideo,
}: GraftcodeOnboardingDashboardTutorialStepsProps) {
  const t = useTranslations('graftcodeOnboarding')
  const isLastStep = activeStepIndex >= steps.length - 1
  const requirementsStepIndex = setupMode === GRAFTCODE_ONBOARDING_SETUP_MODE.agent ? 1 : 0

  return (
    <div
      className={clsx(
        styles.steps,
        contentRefreshing && styles.stepsRefreshing,
        stepMotion === 'forward' && styles.stepsMotionForward,
        stepMotion === 'back' && styles.stepsMotionBack,
        stepMotion === 'swap' && styles.stepsMotionSwap
      )}
    >
      {visibleStepIndexes.map((index) => {
        const step = steps[index]
        if (!step) {
          return null
        }

        const isLastVisible = index === visibleStepIndexes[visibleStepIndexes.length - 1]
        const isLastOverall = index === steps.length - 1
        const isActive = index === activeStepIndex
        const isComplete = index < activeStepIndex
        const isNext = index > activeStepIndex
        const badgeVariant = isComplete ? 'done' : isActive ? 'current' : 'upcoming'
        const showRequirements = isActive && index === requirementsStepIndex && requirementChips.length > 0
        const showTutorialButton = showTutorialButtonOnStep(index)

        return (
          <div
            key={step.order}
            id={getGraftcodeOnboardingStepElementId(index)}
            className={clsx(
              styles.stepBlock,
              index === 0 && styles.stepBlockFirst,
              isNext && styles.stepBlockNext,
              isComplete && styles.stepBlockComplete,
              isActive && styles.stepBlockActive
            )}
          >
            <div className={styles.rail} aria-hidden>
              {index === 0 ? <div className={styles.railTop} /> : null}
              <GraftcodeOnboardingTimelineBadge variant={badgeVariant} />
              {!isLastOverall ? (
                <div className={clsx(styles.railLine, isNext && isLastVisible && styles.railLineFade)} />
              ) : null}
            </div>
            <div className={styles.stepBody}>
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <h3 className={clsx(styles.stepTitle, isNext && styles.stepTitleUpcoming)}>{step.title}</h3>
                  {showTutorialButton ? (
                    <button type="button" className={styles.playButton} onClick={onReopenTutorialVideo}>
                      <span className={styles.playButtonIcon} aria-hidden>
                        <Image
                          src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/tutorial-play-icon.svg`}
                          alt=""
                          width={14}
                          height={14}
                        />
                      </span>
                      {t('dashboard.tutorial')}
                    </button>
                  ) : null}
                </div>

                {isActive ? (
                  <>
                    {showRequirements ? (
                      <>
                        <p className={styles.requirementsLead}>{t('dashboard.requirementsLead')}</p>
                        <div className={styles.chips}>
                          {requirementChips.map((chip) => (
                            <a
                              key={chip.label}
                              className={styles.chip}
                              href={chip.href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {chip.label}
                              <IconComponent icon={Icon.OpenRegular} size={20} />
                            </a>
                          ))}
                        </div>
                      </>
                    ) : null}

                    {step.paragraphs.map((paragraph) => (
                      <p
                        key={paragraph.text}
                        className={clsx(styles.bodyText, paragraph.emphasis && styles.bodyTextStrong)}
                      >
                        {paragraph.text}
                      </p>
                    ))}

                    {step.codeBlocks.map((block) => (
                      <div
                        key={getTutorialCodeBlockKey(step, block)}
                        className={clsx(styles.codeBlock, block.language === 'prompt' && styles.agentPromptBlock)}
                      >
                        {block.language === 'prompt' ? (
                          <GraftcodeOnboardingAgentPrompt code={block.code} label={t('dashboard.agentPromptLabel')} />
                        ) : (
                          <CodeSnippet
                            code={block.code}
                            language={mapCodeLanguage(block.language)}
                            contentVariant={
                              /bash|sh|terminal|powershell|pwsh/i.test(block.language) ? 'terminal' : 'vision-expanded'
                            }
                            labelText={
                              /bash|sh|powershell|pwsh|terminal/i.test(block.language)
                                ? 'terminal'
                                : block.language || 'code'
                            }
                          />
                        )}
                      </div>
                    ))}

                    {step.visionUrl ? (
                      <a
                        className={styles.visionButton}
                        href={step.visionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.openVisionLink}
                      >
                        <Image
                          className={styles.visionButtonAppIcon}
                          src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/vision-app-icon-20.svg`}
                          alt=""
                          width={20}
                          height={20}
                        />
                        <span className={styles.visionButtonLabel}>{t('dashboard.openVision')}</span>
                        <IconComponent icon={Icon.OpenRegular} size={20} aria-hidden />
                      </a>
                    ) : null}

                    <div className={styles.stepNav}>
                      {index > 0 ? (
                        <Button
                          type="button"
                          text={t('dashboard.previous')}
                          variant={ButtonVariant.Outlined}
                          size={ButtonSize.L}
                          className={clsx(styles.stepNavButton, styles.stepNavButtonPrevious)}
                          onClick={onPrevious}
                        />
                      ) : null}
                      {isLastStep ? (
                        <div className={styles.navGroup}>
                          <button type="button" className={styles.outlineButton} onClick={onReopenTutorialVideo}>
                            {t('dashboard.howItWorks')}
                          </button>
                          <button type="button" className={styles.primaryButton} onClick={onCompleteTutorial}>
                            {t('dashboard.checkedResults')}
                            <IconComponent icon={Icon.ArrowRightRegular} size={20} />
                          </button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          text={t('dashboard.next')}
                          variant={ButtonVariant.Primary}
                          size={ButtonSize.L}
                          className={styles.stepNavButton}
                          onClick={onNext}
                        />
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
